import React from 'react';
import { Navigate } from 'react-router-dom';

import AdminHome from 'pages/Admin/AdminHome';
import AdminAnalytics from 'pages/Admin/AdminAnalytics';
import Operations from 'pages/Admin/Operations';
import AISettings from 'pages/Admin/AISettings/AISettings';
import ContentTools from 'pages/Admin/ContentTools';
import ThemePreview from 'pages/Admin/ThemePreview';
import FeedbackReports from 'pages/Admin/FeedbackReports';
import MentorshipTools from 'pages/Admin/MentorshipTools';
import Organizations from 'pages/Admin/Organizations';
import UserTools from 'pages/Admin/UserTools';
import LogIn from 'pages/Login/Login';
import Register from 'pages/Register/Register';
import { ForgotPassword } from 'pages/Login/ForgotPassword';
import { ResetPassword } from 'pages/Login/ResetPassword';
import Terms from 'pages/Terms/Terms';
import Privacy from 'pages/Privacy/Privacy';
import { Dashboard } from 'pages/Home/Dashboard';
import Reports from 'pages/Reports/Reports';
import CourseCompletion from 'pages/CourseCompletion';
import { MyTraining } from 'pages/MyTraining/MyTraining';
import { CourseDetails } from 'pages/MyTraining/CourseDetails';
import Snapshot from 'pages/Snapshot/Snapshot';
import { Resources } from 'pages/Help/Resources';
import { Faq } from 'pages/Help/Faq';
import MentorshipAdminDashboard from 'pages/Mentorship/AdminDashboard';
import { Contact } from 'pages/Contact/Contact';
import { Toolkit } from 'pages/Toolkit/Toolkit';
import NotFound from 'pages/NotFound/NotFound';
import { TimezoneTest } from 'components/TimezoneTest';
import QuizResults from 'pages/QuizResults/QuizResults';
import OrganizationAdmin from 'pages/OrganizationAdmin/OrganizationAdmin';
import ResponseDataPage from 'pages/OrganizationAdmin/ResponseReports/ResponseReportsPage';
import { MentorshipLandingRoute, MentorshipRoleRoute } from './MentorshipLandingRoute';
import { FlowRoute } from './FlowRoute';
import { AdminRoute, OrganizationAdminRoute, ProtectedRoute, PublicRoute, RequireIntakeRoute } from './routeGuards';
import Profile from 'pages/Profile/Profile';
import MeetingCall from 'pages/Meetings/MeetingCall';
import MeetingQuestionsPanel from 'pages/Meetings/MeetingQuestionsPanel';
import MeetingInspirePanel from 'pages/Meetings/MeetingInspirePanel';
import WiseFriendControlsPanel from 'pages/Meetings/WiseFriendControlsPanel';
import ProgramSurveys from 'pages/ProgramSurveys/ProgramSurveys';

export type GuardComponent = React.ComponentType<{ children: React.ReactNode }>;

export type RouteMeta = {
  label?: string;
  includeInSitemap?: boolean;
};

export type HomeContentWidth = 'standard' | 'narrow';

export type RouteLayout = 'home' | 'admin';

export type RouteEntry = {
  path: string;
  element: React.ReactNode;
  layout?: RouteLayout;
  contentWidth?: HomeContentWidth;
  meta?: RouteMeta;
};

export type RouteGroup = {
  guards: GuardComponent[];
  routes: RouteEntry[];
};

const redirect = (to: string) => <Navigate to={to} replace />;

export const MEETING_CALL_PATH = '/meetings/:meetingId';

export const ROUTE_GROUPS: RouteGroup[] = [
  {
    guards: [],
    routes: [
      {
        path: '/',
        element: redirect('/home'),
        meta: { includeInSitemap: false },
      },
      {
        path: '/terms',
        element: <Terms />,
        layout: 'home',
        meta: { label: 'Terms of Service', includeInSitemap: true },
      },
      {
        path: '/privacy',
        element: <Privacy />,
        layout: 'home',
        meta: { label: 'Privacy Policy', includeInSitemap: true },
      },
      {
        path: MEETING_CALL_PATH,
        element: <MeetingCall />,
        meta: { label: 'Meeting', includeInSitemap: false },
      },
      {
        path: '/meetings/:meetingId/questions-panel',
        element: <MeetingQuestionsPanel />,
        meta: { label: 'Meeting Questions', includeInSitemap: false },
      },
      {
        path: '/meetings/:meetingId/inspire-panel',
        element: <MeetingInspirePanel />,
        meta: { label: 'Inspire Card', includeInSitemap: false },
      },
      {
        path: '/meetings/:meetingId/wisefriend-controls',
        element: <WiseFriendControlsPanel />,
        meta: { label: 'WiseFriend Controls', includeInSitemap: false },
      },
      {
        path: '*',
        element: <NotFound />,
        layout: 'home',
        meta: { label: 'Page Not Found', includeInSitemap: false },
      },
    ],
  },
  {
    guards: [PublicRoute],
    routes: [
      {
        path: '/login',
        element: <LogIn />,
        meta: { label: 'Login', includeInSitemap: false },
      },
      {
        path: '/forgot-password',
        element: <ForgotPassword />,
        meta: { label: 'Forgot Password', includeInSitemap: false },
      },
      {
        path: '/reset-password',
        element: <ResetPassword />,
        meta: { label: 'Reset Password', includeInSitemap: false },
      },
      {
        path: '/register',
        element: <Register />,
        meta: { label: 'Register', includeInSitemap: false },
      },
    ],
  },
  {
    guards: [ProtectedRoute],
    routes: [
      {
        path: '/home',
        element: <Dashboard />,
        layout: 'home',
        meta: { label: 'Home', includeInSitemap: true },
      },
      {
        path: '/program-surveys',
        element: <ProgramSurveys />,
        layout: 'home',
        meta: { label: 'Program check-ins', includeInSitemap: false },
      },
      {
        path: '/timezone-test',
        element: <TimezoneTest />,
        layout: 'home',
        meta: { label: 'Timezone Test', includeInSitemap: false },
      },
      {
        path: '/daily-checkin-customization',
        element: redirect('/home'),
        meta: { includeInSitemap: false },
      },
      {
        path: '/course_complete',
        element: <CourseCompletion />,
        layout: 'home',
        meta: { label: 'Course Completion', includeInSitemap: true },
      },
      {
        path: '/training',
        element: <MyTraining />,
        layout: 'home',
        meta: { label: 'Training', includeInSitemap: true },
      },
      {
        path: '/training/:courseId',
        element: <CourseDetails />,
        layout: 'home',
        meta: { label: 'Course details', includeInSitemap: false },
      },
      {
        path: '/snapshot',
        element: <Snapshot />,
        layout: 'home',
        meta: { label: 'Snapshot', includeInSitemap: true },
      },
      {
        path: '/toolkit',
        element: <Toolkit />,
        layout: 'home',
        meta: { label: 'Toolkit', includeInSitemap: true },
      },
      {
        path: '/mentorship',
        element: <MentorshipLandingRoute />,
        layout: 'home',
        meta: { label: 'Mentorship Dashboard', includeInSitemap: false },
      },
      {
        path: '/mentorship/director',
        element: (
          <MentorshipRoleRoute allowedRoles={['cohort_director', 'cohort_admin']}>
            <MentorshipAdminDashboard />
          </MentorshipRoleRoute>
        ),
        layout: 'home',
        meta: { label: 'Cohort Admin Dashboard', includeInSitemap: false },
      },
      {
        path: '/help/resources',
        element: <Resources />,
        layout: 'home',
        contentWidth: 'narrow',
        meta: { label: 'Resources', includeInSitemap: true },
      },
      {
        path: '/help/faq',
        element: <Faq />,
        layout: 'home',
        contentWidth: 'narrow',
        meta: { label: 'FAQ', includeInSitemap: true },
      },
      {
        path: '/profile',
        element: <Profile />,
        layout: 'home',
        meta: { label: 'Profile', includeInSitemap: false },
      },
      {
        path: '/profile/:userId',
        element: <Profile />,
        layout: 'home',
        meta: { label: 'Profile', includeInSitemap: false },
      },
      {
        path: '/contact',
        element: <Contact />,
        layout: 'home',
        contentWidth: 'narrow',
        meta: { label: 'Contact', includeInSitemap: true },
      },
      {
        path: '/organization/admin',
        element: (
          <OrganizationAdminRoute>
            <OrganizationAdmin />
          </OrganizationAdminRoute>
        ),
        layout: 'home',
        meta: { label: 'Organization Admin', includeInSitemap: false },
      },
      {
        path: '/organization/admin/response-data',
        element: (
          <OrganizationAdminRoute>
            <ResponseDataPage />
          </OrganizationAdminRoute>
        ),
        layout: 'home',
        meta: { label: 'Organization Responses', includeInSitemap: false },
      },
      {
        path: '/organization/admin/response-reports',
        element: redirect('/organization/admin/response-data'),
        layout: 'home',
        meta: { includeInSitemap: false },
      },
      {
        path: '/courses/:courseId/flows/:flowName',
        element: <FlowRoute />,
        meta: { label: 'Course Module', includeInSitemap: false },
      },
      {
        path: '/flows/:flowName',
        element: <FlowRoute />,
        meta: { label: 'Module', includeInSitemap: false },
      },
      {
        path: '/daily-checkin-complete',
        element: redirect('/home'),
        meta: { includeInSitemap: false },
      },
      {
        path: '/quiz-results/:flowId',
        element: <QuizResults />,
        layout: 'home',
        meta: { label: 'Quiz Results', includeInSitemap: false },
      },
    ],
  },
  {
    guards: [ProtectedRoute, RequireIntakeRoute],
    routes: [
      {
        path: '/reports',
        element: <Reports />,
        layout: 'home',
        meta: { label: 'Reports', includeInSitemap: true },
      },
    ],
  },
  {
    guards: [AdminRoute],
    routes: [
      {
        path: '/admin',
        element: <AdminHome />,
        layout: 'admin',
        meta: { label: 'Admin Home', includeInSitemap: false },
      },
      {
        path: '/admin/analytics',
        element: <AdminAnalytics />,
        layout: 'admin',
        meta: { label: 'Analytics', includeInSitemap: false },
      },
      {
        path: '/admin/content',
        element: <ContentTools />,
        layout: 'admin',
        meta: { label: 'Content', includeInSitemap: false },
      },
      {
        path: '/admin/themes/:themeId/preview',
        element: <ThemePreview />,
        meta: { label: 'Theme Preview', includeInSitemap: false },
      },
      {
        path: '/admin/feedback',
        element: <FeedbackReports />,
        layout: 'admin',
        meta: { label: 'Feedback', includeInSitemap: false },
      },
      {
        path: '/admin/cohorts',
        element: <MentorshipTools />,
        layout: 'admin',
        meta: { label: 'Cohorts', includeInSitemap: false },
      },
      {
        path: '/admin/organizations',
        element: <Organizations />,
        layout: 'admin',
        meta: { label: 'Organizations', includeInSitemap: false },
      },
      {
        path: '/admin/users',
        element: <UserTools />,
        layout: 'admin',
        meta: { label: 'Users', includeInSitemap: false },
      },
      {
        path: '/admin/operations',
        element: <Operations />,
        layout: 'admin',
        meta: { label: 'Operations', includeInSitemap: false },
      },
      {
        path: '/admin/ai-settings',
        element: <AISettings />,
        layout: 'admin',
        meta: { label: 'AI Settings', includeInSitemap: false },
      },
    ],
  },
];
